import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
  useMemo,
} from "react";
import styled from "styled-components";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Avatar,
  Button,
  Input,
  message,
  Empty,
  Spin,
  Tag,
  Tooltip,
  Popover,
} from "antd";
import {
  MessageOutlined,
  SendOutlined,
  ArrowLeftOutlined,
  UserOutlined,
  HomeOutlined,
  BuildOutlined,
  BulbOutlined,
  TeamOutlined,
  InfoCircleOutlined,
  PaperClipOutlined,
  LockOutlined,
  ExclamationCircleOutlined,
  PhoneOutlined,
  MailOutlined,
  ToolOutlined,
  DownOutlined,
  UpOutlined,
} from "@ant-design/icons";
import { device, useBreakpoints } from "../lib/breakpoints";
import {
  CLUMSE_AI_DISPLAY_NAME,
  SESSION_STORAGE,
} from "../helpers/constants";
import { doc, getDoc } from "firebase/firestore";
import vendorConversationAPI from "../lib/api/vendorConversationAPI";
import { AuthContext } from "../context/AuthProvider";
import Logo from "../components/general/Logo";
import { db } from "../helpers/firebase";
import ImageUpload from "../components/chat/ImageUpload";

const { TextArea } = Input;

/** True when this turn is from Clumse AI (handles alternate Firestore shapes). */
function isClumseAiMessage(message) {
  if (!message || typeof message !== "object") return false;
  if (message.fromUserType === "ai") return true;
  if (message.fromUserId === "ai_assistant") return true;
  if (String(message.sender || "").toLowerCase() === "ai") return true;
  return false;
}

const VendorConversationPage = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useBreakpoints();
  const queryClient = useQueryClient();
  const { user } = useContext(AuthContext);

  // Debug: Log URL parameters on mount
  useEffect(() => {
    console.log("[VendorConversationPage] Component mounted with URL:", {
      pathname: location.pathname,
      search: location.search,
      conversationIdFromParams: conversationId,
      vendorIdFromUrl: new URLSearchParams(location.search).get("vendorId"),
      fullUrl: `${location.pathname}${location.search}`,
    });
  }, [location.pathname, location.search, conversationId]);

  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [vendorId, setVendorId] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [showImageUpload, setShowImageUpload] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const prevMessagesCountRef = useRef(0);

  // Get vendor ID from URL params or localStorage
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const vendorIdParam = urlParams.get("vendorId");

    if (vendorIdParam) {
      setVendorId(vendorIdParam);
      localStorage.setItem("vendorId", vendorIdParam);
    } else {
      const storedVendorId = localStorage.getItem("vendorId");
      if (storedVendorId) {
        setVendorId(storedVendorId);
      }
    }
  }, []);

  // If not authenticated, redirect to login and return back
  useEffect(() => {
    // Allow unauthenticated access if vendorId is provided via query/localStorage
    const urlParams = new URLSearchParams(location.search);
    const vendorIdFromUrl = urlParams.get("vendorId");
    const vendorIdFromStorage = localStorage.getItem("vendorId");
    const hasVendorId = vendorIdFromUrl || vendorIdFromStorage;

    if (!user && !hasVendorId) {
      const returnUrl = `${location.pathname}${location.search}`;
      try {
        sessionStorage.setItem(SESSION_STORAGE.redirectUrl, returnUrl);
      } catch (e) {
        // In non-browser contexts sessionStorage may not exist
        // eslint-disable-next-line no-console
        console.warn("Unable to set redirectUrl in sessionStorage", e);
      }
      navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
    }
  }, [user, navigate, location.pathname, location.search]);

  // Load selected conversation details
  const {
    data: conversationData,
    isLoading: isLoadingConversation,
    isError: isConversationError,
    error: conversationError,
    refetch: refetchConversation,
  } = useQuery({
    queryKey: ["vendorConversation", conversationId, vendorId],
    queryFn: async () => {
      console.log("[VendorConversationPage] Fetching conversation:", {
        conversationId,
        vendorId,
        url: `${process.env.REACT_APP_API_URL}/conversations/${conversationId}${
          vendorId ? `?vendorId=${vendorId}` : ""
        }`,
      });
      const result = await vendorConversationAPI.getConversation(
        conversationId,
        vendorId || null
      );
      console.log("[VendorConversationPage] Conversation response:", {
        success: result?.success,
        conversationId: result?.conversation?.id,
        conversationType: result?.conversation?.type,
        participants: result?.conversation?.participants,
        messageCount: result?.conversation?.messages?.length,
        expectedConversationId: conversationId,
        matches: result?.conversation?.id === conversationId,
      });
      return result;
    },
    enabled: !!conversationId,
    refetchInterval: 5000, // Refetch every 5 seconds for real-time updates
    retry: false, // Don't retry on access denied errors
  });

  // Load ticket details for human-readable ticket number
  // Note: This may fail if user is not authenticated (vendors can access without login)
  const ticketId = conversationData?.conversation?.context?.relatedTicketId;
  const { data: ticketData } = useQuery({
    queryKey: ["maintenanceTicket", ticketId],
    queryFn: async () => {
      const snap = await getDoc(doc(db, "jobRequests", ticketId));
      if (!snap.exists()) return null;
      return { id: snap.id, ...snap.data() };
    },
    enabled: !!ticketId && !!user,
    retry: false,
  });

  // Set selected conversation when conversationId changes
  useEffect(() => {
    if (
      conversationId &&
      conversationData?.success &&
      conversationData?.conversation
    ) {
      const conversation = conversationData.conversation;

      // CRITICAL CHECK: Verify the conversation ID matches what we requested
      if (conversation.id !== conversationId) {
        console.error("[VendorConversationPage] ⚠️ CONVERSATION ID MISMATCH!", {
          requestedId: conversationId,
          returnedId: conversation.id,
          conversationType: conversation.type,
          message: "Backend returned a different conversation than requested!",
        });
        message.error(
          `Warning: The conversation ID doesn't match. Expected ${conversationId}, got ${conversation.id}`
        );
      }

      // Check if this is a maintenance group chat (which is wrong)
      if (
        conversation.type === "maintenance_group" ||
        conversation.handymanIds ||
        conversation.ticketId
      ) {
        console.error("[VendorConversationPage] ⚠️ WRONG CONVERSATION TYPE!", {
          conversationId: conversation.id,
          type: conversation.type,
          hasHandymanIds: !!conversation.handymanIds,
          hasTicketId: !!conversation.ticketId,
          message:
            "This appears to be a maintenance group chat, not the regular tenant chat!",
        });
        message.warning(
          "Warning: This appears to be a maintenance group chat. You should be in the regular tenant chat instead."
        );
      }

      setSelectedConversation(conversation);

      // Debug: Log full conversation data to see what backend returns
      const messages = conversation.messages || [];

      const messagesByType = {
        ai: messages.filter((m) => isClumseAiMessage(m)),
        tenant: messages.filter((m) => m.fromUserType === "tenant"),
        landlord: messages.filter((m) => m.fromUserType === "landlord"),
        vendor: messages.filter((m) => m.fromUserType === "vendor"),
        other: messages.filter(
          (m) =>
            !["ai", "tenant", "landlord", "vendor"].includes(m.fromUserType)
        ),
      };

      console.log(
        "[VendorConversationPage] Full conversation data from backend - BREAKDOWN:"
      );
      console.log(`  Conversation ID: ${conversation.id}`);
      console.log(`  Total Messages: ${messages.length}`);
      console.log(`  Participants:`, conversation.participants || []);
      console.log(`  Messages by Type Count:`, {
        ai: messagesByType.ai.length,
        tenant: messagesByType.tenant.length,
        landlord: messagesByType.landlord.length,
        vendor: messagesByType.vendor.length,
        other: messagesByType.other.length,
      });
      console.log(
        "  AI Messages:",
        messagesByType.ai.map((m) => ({
          id: m.id,
          fromUserId: m.fromUserId,
          content: typeof m.content === "object" ? m.content?.text : m.content,
        }))
      );
      console.log(
        "  Tenant Messages:",
        messagesByType.tenant.map((m) => ({
          id: m.id,
          fromUserId: m.fromUserId,
          content: typeof m.content === "object" ? m.content?.text : m.content,
        }))
      );
      console.log(
        "  Landlord Messages:",
        messagesByType.landlord.map((m) => ({
          id: m.id,
          fromUserId: m.fromUserId,
          content: typeof m.content === "object" ? m.content?.text : m.content,
        }))
      );
      console.log(
        "  Vendor Messages:",
        messagesByType.vendor.map((m) => ({
          id: m.id,
          fromUserId: m.fromUserId,
          content: typeof m.content === "object" ? m.content?.text : m.content,
        }))
      );
      console.log(
        "  Other Messages:",
        messagesByType.other.map((m) => ({
          id: m.id,
          fromUserType: m.fromUserType,
          fromUserId: m.fromUserId,
          content: typeof m.content === "object" ? m.content?.text : m.content,
        }))
      );
      console.log(
        "  All Messages (full):",
        messages.map((msg) => ({
          id: msg.id,
          fromUserType: msg.fromUserType,
          fromUserId: msg.fromUserId,
          fromUserName: msg.fromUserName,
          content:
            typeof msg.content === "object"
              ? msg.content.text || JSON.stringify(msg.content)
              : msg.content,
          message: msg.message,
          timestamp: msg.timestamp || msg.createdAt,
        }))
      );

      // Debug: Log message structure to identify image field names
      const messagesWithImages = messages.filter(
        (msg) => msg.images || msg.imageUrls || msg.attachments
      );
      if (messagesWithImages.length > 0) {
        console.log(
          "[VendorConversationPage] Messages with images found:",
          messagesWithImages.map((msg) => ({
            id: msg.id,
            hasImages: !!msg.images,
            hasImageUrls: !!msg.imageUrls,
            hasAttachments: !!msg.attachments,
            images: msg.images,
            imageUrls: msg.imageUrls,
            attachments: msg.attachments,
            allKeys: Object.keys(msg),
          }))
        );
      }
    }
  }, [conversationId, conversationData]);

  // Track scroll position
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const nearBottom = scrollHeight - scrollTop - clientHeight <= 5;
      setIsAtBottom(nearBottom);
    };

    handleScroll();
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [selectedConversation]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    const conversation = conversationData?.conversation;
    if (!conversation?.messages) return;

    const newCount = conversation.messages.length;
    const prevCount = prevMessagesCountRef.current;
    const hasNew = newCount > prevCount;

    if (hasNew && isAtBottom) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }

    prevMessagesCountRef.current = newCount;
  }, [
    conversationData?.conversation,
    conversationData?.conversation?.messages,
    isAtBottom,
  ]);

  // Jump to bottom when conversation is selected
  useEffect(() => {
    if (selectedConversation && !isLoadingConversation) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
      }, 100);
    }
  }, [selectedConversation, selectedConversation?.id, isLoadingConversation]);

  const formatTime = useCallback((timestamp) => {
    if (!timestamp) return "Just now";

    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return "Just now";

      const now = new Date();
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));

      if (diffInMinutes < 1) return "Just now";
      if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

      const diffInHours = Math.floor(diffInMinutes / 60);
      if (diffInHours < 24) return `${diffInHours}h ago`;

      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays < 7) return `${diffInDays}d ago`;

      return date.toLocaleDateString();
    } catch (error) {
      console.error("Error formatting time:", error);
      return "Just now";
    }
  }, []);

  const handleSendMessage = async () => {
    console.log("[VendorConversationPage] handleSendMessage called", {
      hasMessage: !!newMessage.trim(),
      hasImages: selectedImages.length > 0,
      hasConversation: !!selectedConversation,
      hasVendorId: !!vendorId,
      conversationId: selectedConversation?.id,
      vendorId,
    });

    if (
      (!newMessage.trim() && selectedImages.length === 0) ||
      !selectedConversation ||
      !vendorId
    ) {
      console.warn(
        "[VendorConversationPage] Cannot send message - missing requirements:",
        {
          hasMessage: !!newMessage.trim(),
          hasImages: selectedImages.length > 0,
          hasConversation: !!selectedConversation,
          hasVendorId: !!vendorId,
        }
      );
      return;
    }

    try {
      setIsSending(true);

      // Get image URLs from selected images
      const imageUrls = selectedImages.map((img) => img.url);

      // If no message text but there are images, send a default message
      // Backend may require a non-empty message string
      const messageText =
        newMessage.trim() || (imageUrls.length > 0 ? "(Image)" : "");

      console.log("[VendorConversationPage] Sending message:", {
        conversationId: selectedConversation.id,
        conversationIdFromUrl: conversationId,
        vendorId,
        messageText,
        imageUrls,
        imageCount: imageUrls.length,
        conversationType: selectedConversation.type,
        participants: selectedConversation.participants,
        participantCount: selectedConversation.participants?.length,
      });

      // CRITICAL: Verify we're using the correct conversation ID
      if (selectedConversation.id !== conversationId) {
        console.error(
          "[VendorConversationPage] ⚠️ CONVERSATION ID MISMATCH WHEN SENDING!",
          {
            selectedConversationId: selectedConversation.id,
            urlConversationId: conversationId,
            message: "Using wrong conversation ID to send message!",
          }
        );
        message.error(
          "Error: Conversation ID mismatch. Message may not be sent to the correct conversation."
        );
      }

      const result = await vendorConversationAPI.sendVendorMessage(
        selectedConversation.id,
        vendorId,
        messageText,
        imageUrls
      );

      console.log("[VendorConversationPage] Message send API response:", {
        success: result?.success,
        messageId: result?.messageId,
        conversationId: result?.conversationId,
        fullResponse: result,
      });

      setNewMessage("");
      setSelectedImages([]);
      setShowImageUpload(false);

      // Small delay to ensure backend has processed the message with images
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Refetch conversation to get updated messages
      await refetchConversation();

      message.success("Message sent successfully!");
    } catch (error) {
      console.error("[VendorConversationPage] Error sending message:", error);
      console.error("[VendorConversationPage] Error details:", {
        message: error.message,
        stack: error.stack,
      });
      message.error(error.message || "Failed to send message");
    } finally {
      setIsSending(false);
    }
  };

  const getParticipantDisplayName = (participant) => {
    if (participant.userType === "ai") {
      return "Clumse AI";
    }
    return participant.userName || participant.userEmail || "Unknown";
  };

  const getParticipantIcon = (participant) => {
    switch (participant.userType) {
      case "ai":
        return (
          <img
            src="/icons/leo/leo.png"
            alt="Clumse AI"
            style={{ width: "16px", height: "16px" }}
          />
        );
      case "vendor":
      case "handyman":
        return <ToolOutlined />;
      case "landlord":
        return <UserOutlined />;
      case "tenant":
        return <HomeOutlined />;
      default:
        return <UserOutlined />;
    }
  };

  const getMessageSenderDisplayName = (message, participants) => {
    if (isClumseAiMessage(message)) {
      return CLUMSE_AI_DISPLAY_NAME;
    }
    const participant = participants?.find(
      (p) =>
        p.userId === message.fromUserId && p.userType === message.fromUserType
    );
    return participant?.userName || participant?.userEmail || "Unknown";
  };

  const currentConversation =
    conversationData?.conversation || selectedConversation;
  const messages = useMemo(
    () => currentConversation?.messages || [],
    [currentConversation?.messages]
  );
  const participants = useMemo(
    () => currentConversation?.participants || [],
    [currentConversation?.participants]
  );

  // Extract landlord information for contact section
  const landlordParticipant = useMemo(() => {
    return participants.find((p) => p.userType === "landlord");
  }, [participants]);

  // Debug: Log all messages to check for images
  useEffect(() => {
    if (messages.length > 0) {
      console.log(
        "[VendorConversationPage] All messages:",
        messages.map((msg) => ({
          id: msg.id,
          message: msg.message || msg.content,
          fromUserId: msg.fromUserId,
          fromUserType: msg.fromUserType,
          timestamp: msg.timestamp || msg.createdAt,
          images: msg.images,
          imageUrls: msg.imageUrls,
          attachments: msg.attachments,
          hasImages: !!(msg.images || msg.imageUrls || msg.attachments),
          allKeys: Object.keys(msg),
          fullMessage: msg,
        }))
      );
    }
  }, [messages]);

  if (!vendorId) {
    return (
      <Container>
        <EmptyState>
          <EmptyStateIcon>
            <InfoCircleOutlined />
          </EmptyStateIcon>
          <EmptyStateTitle>Access Required</EmptyStateTitle>
          <EmptyStateSubtitle>
            Please access this page through the link provided in your
            maintenance notification email.
          </EmptyStateSubtitle>
        </EmptyState>
      </Container>
    );
  }

  // Check if we have an access denied error
  const isAccessDenied =
    isConversationError &&
    (conversationError?.message?.toLowerCase().includes("access denied") ||
      conversationError?.message
        ?.toLowerCase()
        .includes("conversation not found") ||
      conversationError?.message
        ?.toLowerCase()
        .includes("not found or access denied"));

  if (isAccessDenied) {
    return (
      <Container>
        <Header>
          <LogoContainer>
            <Logo />
          </LogoContainer>
        </Header>
        <AccessDeniedContainer>
          <AccessDeniedContent>
            <AccessDeniedIconWrapper>
              <LockOutlined />
            </AccessDeniedIconWrapper>
            <AccessDeniedTitle>Access Denied</AccessDeniedTitle>
            <AccessDeniedSubtitle>
              You don't have permission to view this conversation. This may
              happen if:
            </AccessDeniedSubtitle>
            <AccessDeniedList>
              <AccessDeniedItem>
                <ExclamationCircleOutlined />
                <span>The conversation link has expired or is invalid</span>
              </AccessDeniedItem>
              <AccessDeniedItem>
                <ExclamationCircleOutlined />
                <span>You're not assigned to this maintenance ticket</span>
              </AccessDeniedItem>
              <AccessDeniedItem>
                <ExclamationCircleOutlined />
                <span>The ticket has been closed or removed</span>
              </AccessDeniedItem>
            </AccessDeniedList>
            <AccessDeniedFooter>
              <AccessDeniedFooterText>
                Please contact the property manager or use the link from your
                most recent notification email.
              </AccessDeniedFooterText>
            </AccessDeniedFooter>
          </AccessDeniedContent>
        </AccessDeniedContainer>
      </Container>
    );
  }

  // Get human-readable ticket number/title
  const getTicketDisplay = () => {
    if (ticketData) {
      // Use ticket title if available, otherwise use ticket ID
      return ticketData.title || `Ticket #${ticketData.id.slice(-8)}`;
    }
    if (ticketId) {
      // Fallback to just the ticket ID if we can't load the ticket
      return `Ticket #${ticketId.slice(-8)}`;
    }
    return "Maintenance Ticket";
  };

  return (
    <Container>
      <Header>
        <HeaderContent>
          <LogoContainer>
            <Logo color="white" />
          </LogoContainer>
          <HeaderDescription>
            Maintenance Ticket Communication
          </HeaderDescription>
        </HeaderContent>
      </Header>

      <MainLayout>
        {/* Conversation View - Full Width */}
        <ConversationView>
          {!currentConversation ? (
            <EmptyState>
              <EmptyStateIcon>
                <MessageOutlined />
              </EmptyStateIcon>
              <EmptyStateTitle>Loading Conversation</EmptyStateTitle>
              <EmptyStateSubtitle>
                Please wait while we load the maintenance conversation...
              </EmptyStateSubtitle>
            </EmptyState>
          ) : (
            <>
              {/* Conversation Header with Ticket Description */}
              <ConversationHeader>
                <ConversationInfo>
                  <ConversationTitleHeader>
                    {getTicketDisplay()}
                  </ConversationTitleHeader>

                  <ParticipantsSection>
                    <ParticipantsTags>
                      {participants.map((p) => {
                        // Get role label based on userType
                        const getRoleLabel = (userType) => {
                          switch (userType) {
                            case "landlord":
                              return "Landlord";
                            case "tenant":
                              return "Tenant";
                            case "vendor":
                            case "handyman":
                              return "Service Team";
                            case "ai":
                              return "Clumse AI";
                            default:
                              return userType || "Participant";
                          }
                        };

                        const isLandlord =
                          p.userType === "landlord" && landlordParticipant;

                        const contactContent = isLandlord ? (
                          <ContactPopoverContent>
                            <ContactPopoverTitle>
                              <UserOutlined style={{ marginRight: "6px" }} />
                              Property Manager Contact
                            </ContactPopoverTitle>
                            <ContactPopoverInfo>
                              <ContactPopoverItem>
                                <ContactPopoverLabel>
                                  <UserOutlined />
                                  Name:
                                </ContactPopoverLabel>
                                <ContactPopoverValue>
                                  {landlordParticipant.userName ||
                                    "Property Manager"}
                                </ContactPopoverValue>
                              </ContactPopoverItem>
                              {landlordParticipant.userEmail && (
                                <ContactPopoverItem>
                                  <ContactPopoverLabel>
                                    <MailOutlined />
                                    Email:
                                  </ContactPopoverLabel>
                                  <ContactPopoverValue>
                                    <ContactLink
                                      href={`mailto:${landlordParticipant.userEmail}`}
                                    >
                                      {landlordParticipant.userEmail}
                                    </ContactLink>
                                  </ContactPopoverValue>
                                </ContactPopoverItem>
                              )}
                              {landlordParticipant.phoneNumber && (
                                <ContactPopoverItem>
                                  <ContactPopoverLabel>
                                    <PhoneOutlined />
                                    Phone:
                                  </ContactPopoverLabel>
                                  <ContactPopoverValue>
                                    <ContactLink
                                      href={`tel:${landlordParticipant.phoneNumber}`}
                                    >
                                      {landlordParticipant.phoneNumber}
                                    </ContactLink>
                                  </ContactPopoverValue>
                                </ContactPopoverItem>
                              )}
                            </ContactPopoverInfo>
                          </ContactPopoverContent>
                        ) : null;

                        const tagContent = (
                          <ParticipantTag
                            key={p.userId}
                            $userType={p.userType}
                            $clickable={isLandlord}
                          >
                            {getParticipantIcon(p)}
                            <span
                              style={{
                                marginLeft: "4px",
                                fontSize: "0.75rem",
                              }}
                            >
                              {getParticipantDisplayName(p)}
                            </span>
                            <span
                              style={{
                                marginLeft: "0px",
                                fontSize: "0.75rem",
                                opacity: 0.8,
                                fontWeight: 500,
                              }}
                            >
                              ({getRoleLabel(p.userType)})
                            </span>
                            {isLandlord && (
                              <DownOutlined
                                style={{
                                  marginLeft: "4px",
                                  fontSize: "0.7rem",
                                  opacity: 0.7,
                                }}
                              />
                            )}
                          </ParticipantTag>
                        );

                        if (isLandlord && contactContent) {
                          return (
                            <Popover
                              key={p.userId}
                              content={contactContent}
                              trigger="click"
                              placement="bottomLeft"
                              overlayStyle={{
                                maxWidth: "300px",
                                border: "1px solid #000000",
                              }}
                            >
                              {tagContent}
                            </Popover>
                          );
                        }

                        return tagContent;
                      })}
                    </ParticipantsTags>
                  </ParticipantsSection>
                </ConversationInfo>
              </ConversationHeader>

              {/* Messages */}
              <MessagesContainer ref={messagesContainerRef}>
                {isLoadingConversation ? (
                  <LoadingContainer>
                    <Spin size="large" />
                    <LoadingText>Loading messages...</LoadingText>
                  </LoadingContainer>
                ) : messages.length === 0 ? (
                  <EmptyState>
                    <EmptyText>No messages yet</EmptyText>
                    <EmptySubtext>Be the first to send a message!</EmptySubtext>
                  </EmptyState>
                ) : (
                  (() => {
                    // Debug: Log what messages are about to be rendered
                    const aiMessages = messages.filter(
                      (m) => isClumseAiMessage(m)
                    );
                    const tenantMessages = messages.filter(
                      (m) => m.fromUserType === "tenant"
                    );
                    const landlordMessages = messages.filter(
                      (m) => m.fromUserType === "landlord"
                    );
                    const vendorMessages = messages.filter(
                      (m) => m.fromUserType === "vendor"
                    );
                    const otherMessages = messages.filter(
                      (m) =>
                        !["ai", "tenant", "landlord", "vendor"].includes(
                          m.fromUserType
                        )
                    );

                    console.log(
                      "[VendorConversationPage] Rendering messages - BREAKDOWN:"
                    );
                    console.log(`  Total: ${messages.length}`);
                    console.log(
                      `  AI: ${aiMessages.length}`,
                      aiMessages.map((m) => ({
                        id: m.id,
                        fromUserId: m.fromUserId,
                      }))
                    );
                    console.log(
                      `  Tenant: ${tenantMessages.length}`,
                      tenantMessages.map((m) => ({
                        id: m.id,
                        fromUserId: m.fromUserId,
                      }))
                    );
                    console.log(
                      `  Landlord: ${landlordMessages.length}`,
                      landlordMessages.map((m) => ({
                        id: m.id,
                        fromUserId: m.fromUserId,
                      }))
                    );
                    console.log(
                      `  Vendor: ${vendorMessages.length}`,
                      vendorMessages.map((m) => ({
                        id: m.id,
                        fromUserId: m.fromUserId,
                      }))
                    );
                    console.log(
                      `  Other: ${otherMessages.length}`,
                      otherMessages.map((m) => ({
                        id: m.id,
                        fromUserType: m.fromUserType,
                        fromUserId: m.fromUserId,
                      }))
                    );
                    console.log(
                      "[VendorConversationPage] All message details:",
                      messages.map((m) => ({
                        id: m.id,
                        fromUserType: m.fromUserType,
                        fromUserId: m.fromUserId,
                        fromUserName: m.fromUserName,
                        content:
                          typeof m.content === "object"
                            ? m.content?.text
                            : m.content,
                        message: m.message,
                      }))
                    );
                    return messages.map((msg) => {
                      const isVendorMessage =
                        msg.fromUserType === "vendor" &&
                        msg.fromUserId === vendorId;
                      const senderName = getMessageSenderDisplayName(
                        msg,
                        participants
                      );

                      // Handle different possible image field names and nested structures
                      // Images can be at: msg.images, msg.content.images, msg.message.images, msg.imageUrls, msg.attachments
                      let messageImages = [];
                      if (msg.images && Array.isArray(msg.images)) {
                        messageImages = msg.images;
                      } else if (
                        msg.content?.images &&
                        Array.isArray(msg.content.images)
                      ) {
                        messageImages = msg.content.images;
                      } else if (
                        msg.message?.images &&
                        Array.isArray(msg.message.images)
                      ) {
                        messageImages = msg.message.images;
                      } else if (
                        msg.imageUrls &&
                        Array.isArray(msg.imageUrls)
                      ) {
                        messageImages = msg.imageUrls;
                      } else if (
                        msg.attachments &&
                        Array.isArray(msg.attachments)
                      ) {
                        messageImages = msg.attachments;
                      }

                      // Debug log for messages with images (only log once per message)
                      if (messageImages.length > 0 && !msg._imagesLogged) {
                        console.log(
                          "[VendorConversationPage] Message with images found:",
                          {
                            messageId: msg.id,
                            images: messageImages,
                            source: msg.images
                              ? "msg.images"
                              : msg.content?.images
                              ? "msg.content.images"
                              : msg.message?.images
                              ? "msg.message.images"
                              : msg.imageUrls
                              ? "msg.imageUrls"
                              : "msg.attachments",
                            allFields: Object.keys(msg),
                            fullMessage: msg,
                          }
                        );
                        msg._imagesLogged = true; // Prevent repeated logging
                      }

                      return (
                        <MessageBubble key={msg.id} $isVendor={isVendorMessage}>
                          <MessageAvatar $isVendor={isVendorMessage}>
                            <Avatar
                              src={
                                isClumseAiMessage(msg)
                                  ? "/icons/leo/leo.png"
                                  : undefined
                              }
                              icon={
                                isClumseAiMessage(msg) ? undefined : (
                                  <UserOutlined />
                                )
                              }
                              style={{
                                backgroundColor:
                                  isClumseAiMessage(msg)
                                    ? "var(--color-primary)"
                                    : msg.fromUserType === "vendor"
                                    ? "#52c41a"
                                    : "#1890ff",
                              }}
                            />
                          </MessageAvatar>
                          <MessageContent $isVendor={isVendorMessage}>
                            <MessageHeader $isVendor={isVendorMessage}>
                              {isVendorMessage ? (
                                <>
                                  <MessageTime>
                                    {formatTime(msg.timestamp || msg.createdAt)}
                                  </MessageTime>
                                  <MessageSender>You</MessageSender>
                                </>
                              ) : (
                                <>
                                  <MessageSender>{senderName}</MessageSender>
                                  <MessageTime>
                                    {formatTime(msg.timestamp || msg.createdAt)}
                                  </MessageTime>
                                </>
                              )}
                            </MessageHeader>
                            {(() => {
                              // Extract message text content
                              let messageText = "";
                              if (typeof msg.message === "string") {
                                messageText = msg.message;
                              } else if (typeof msg.content === "string") {
                                messageText = msg.content;
                              } else if (
                                msg.content &&
                                typeof msg.content === "object"
                              ) {
                                messageText =
                                  msg.content.text || msg.content.content || "";
                              } else if (
                                msg.message &&
                                typeof msg.message === "object"
                              ) {
                                messageText =
                                  msg.message.text || msg.message.content || "";
                              }

                              // Only render MessageText if there's actual text content
                              return messageText && messageText.trim() ? (
                                <MessageText
                                  $isVendor={isVendorMessage}
                                  $hasImages={messageImages.length > 0}
                                >
                                  {messageText}
                                </MessageText>
                              ) : null;
                            })()}
                            {/* Display images if present */}
                            {messageImages.length > 0 && (
                              <MessageImages $isVendor={isVendorMessage}>
                                {messageImages.map((imageUrl, idx) => {
                                  // Handle both string URLs and objects with url property
                                  const url =
                                    typeof imageUrl === "string"
                                      ? imageUrl
                                      : imageUrl.url || imageUrl.src;
                                  return (
                                    <MessageImage
                                      key={idx}
                                      src={url}
                                      alt={`Attachment ${idx + 1}`}
                                      onClick={() => window.open(url, "_blank")}
                                      $isVendor={isVendorMessage}
                                      onError={(e) => {
                                        console.error(
                                          "[VendorConversationPage] Failed to load image:",
                                          url,
                                          e
                                        );
                                      }}
                                    />
                                  );
                                })}
                              </MessageImages>
                            )}
                          </MessageContent>
                        </MessageBubble>
                      );
                    });
                  })()
                )}
                <div ref={messagesEndRef} />
              </MessagesContainer>

              {/* Input Area */}
              <InputArea>
                {showImageUpload && (
                  <ImageUploadSection>
                    <ImageUpload
                      onImagesChange={setSelectedImages}
                      maxImages={3}
                      disabled={isSending}
                    />
                  </ImageUploadSection>
                )}
                <InputContainer>
                  <AttachmentButton
                    type="text"
                    icon={<PaperClipOutlined />}
                    onClick={() => setShowImageUpload(!showImageUpload)}
                    disabled={isSending}
                    $active={showImageUpload}
                  />
                  <TextArea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message..."
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    onPressEnter={(e) => {
                      if (!e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                  />
                  <SendButton
                    type="default"
                    icon={<SendOutlined />}
                    onClick={handleSendMessage}
                    loading={isSending}
                    disabled={
                      (!newMessage.trim() && selectedImages.length === 0) ||
                      isSending
                    }
                  >
                    {!isMobile && "Send"}
                  </SendButton>
                </InputContainer>
                <DisclaimerText>
                  Clumse AI can make mistakes. Check important info.
                </DisclaimerText>
              </InputArea>
            </>
          )}
        </ConversationView>
      </MainLayout>
    </Container>
  );
};

export default VendorConversationPage;

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #ffffff;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 2rem;
  border-bottom: 2px solid rgba(44, 95, 45, 0.1);
  background: linear-gradient(
    135deg,
    var(--color-primary) 0%,
    var(--color-primary-dark) 100%
  );
  box-shadow: 0 2px 8px rgba(44, 95, 45, 0.15);
  z-index: 100;

  @media ${device.mobile} {
    padding: 1rem 1.5rem;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  max-width: 1200px;
`;

const LogoContainer = styled.div`
  margin-bottom: 0;

  /* Make logo white for dark header */
  img,
  svg {
    filter: brightness(0) invert(1);
  }
`;

const HeaderSubtitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  text-align: center;
  letter-spacing: 0.5px;
  margin-top: 0.25rem;

  @media ${device.mobile} {
    font-size: 0.9rem;
  }
`;

const HeaderDescription = styled.div`
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.85);
  text-align: center;

  @media ${device.mobile} {
    font-size: 0.8rem;
  }
`;

const MainLayout = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const Sidebar = styled.div`
  width: 350px;
  background: #f8fafc;
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media ${device.mobile} {
    display: none;
  }
`;

const SidebarHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
`;

const SidebarTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

const ConversationsList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
`;

const ConversationItem = styled.div`
  display: flex;
  padding: 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${(props) => (props.$isSelected ? "#e0f2fe" : "white")};
  border: 1px solid ${(props) => (props.$isSelected ? "#0ea5e9" : "#e2e8f0")};
  margin-bottom: 0.5rem;

  &:hover {
    background: ${(props) => (props.$isSelected ? "#e0f2fe" : "#f1f5f9")};
    border-color: ${(props) => (props.$isSelected ? "#0ea5e9" : "#cbd5e1")};
  }
`;

const ConversationAvatar = styled.div`
  margin-right: 0.75rem;
  flex-shrink: 0;
`;

const ConversationContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ConversationTitle = styled.div`
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.25rem;
  font-size: 0.95rem;
`;

const ConversationMeta = styled.div`
  font-size: 0.75rem;
  color: #64748b;
  margin-bottom: 0.5rem;
`;

const ConversationPreview = styled.div`
  font-size: 0.85rem;
  color: #475569;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 0.25rem;
`;

const ConversationTime = styled.div`
  font-size: 0.7rem;
  color: #94a3b8;
`;

const ConversationView = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
`;

const ConversationHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid rgba(44, 95, 45, 0.1);
  background: linear-gradient(135deg, #ffffff 0%, #f8fffe 100%);
  box-shadow: 0 1px 3px rgba(44, 95, 45, 0.05);

  @media ${device.mobile} {
    padding: 1rem 1.5rem;
  }
`;

const ConversationInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ConversationTitleHeader = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-primary-darker);
  margin: 0;
  font-family: var(--font-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  text-align: center;

  &::before {
    content: "";
    display: inline-block;
    width: 4px;
    height: 1.25rem;
    background: var(--color-primary);
    border-radius: 2px;
  }

  @media ${device.mobile} {
    font-size: 1.1rem;
  }
`;

const ParticipantsSection = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;

  @media ${device.mobile} {
    justify-content: center;
  }
`;

const ParticipantsLabel = styled.div`
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
`;

const ParticipantsTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;

  @media ${device.mobile} {
    justify-content: center;
  }
`;

const ParticipantTag = styled(Tag)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: ${(props) => {
    switch (props.$userType) {
      case "ai":
        return "rgba(44, 95, 45, 0.1)";
      case "vendor":
        return "rgba(82, 196, 26, 0.1)";
      case "landlord":
        return "rgba(24, 144, 255, 0.1)";
      case "tenant":
        return "rgba(114, 46, 209, 0.1)";
      default:
        return "rgba(100, 116, 139, 0.1)";
    }
  }};
  color: ${(props) => {
    switch (props.$userType) {
      case "ai":
        return "var(--color-primary)";
      case "vendor":
        return "#52c41a";
      case "landlord":
        return "#1890ff";
      case "tenant":
        return "#722ed1";
      default:
        return "#64748b";
    }
  }};
  border: 1px solid
    ${(props) => {
      switch (props.$userType) {
        case "ai":
          return "rgba(44, 95, 45, 0.2)";
        case "vendor":
          return "rgba(82, 196, 26, 0.2)";
        case "landlord":
          return "rgba(24, 144, 255, 0.2)";
        case "tenant":
          return "rgba(114, 46, 209, 0.2)";
        default:
          return "rgba(100, 116, 139, 0.2)";
      }
    }};
  padding: 0.375rem 0.75rem;
  border-radius: 16px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: ${(props) => (props.$clickable ? "pointer" : "default")};
  transition: all 0.2s ease;

  /* Ensure all text inside (name and parentheses label) are the same size */
  span {
    font-size: 0.75rem;
  }

  @media ${device.mobile} {
    font-size: 0.7rem;

    span {
      font-size: 0.7rem !important;
    }
  }

  &:hover {
    ${(props) =>
      props.$clickable &&
      `
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      opacity: 0.9;
    `}
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem 2rem;
  background: #f8fafc;
  max-width: 768px;
  margin: 0 auto;
  width: 100%;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }

  @media ${device.mobile} {
    padding: 1rem;
  }
`;

const MessageBubble = styled.div`
  display: flex;
  margin-bottom: 1.5rem;
  align-items: flex-start;
  justify-content: ${(props) => (props.$isVendor ? "flex-end" : "flex-start")};
`;

const MessageAvatar = styled.div`
  margin-right: ${(props) => (props.$isVendor ? "0" : "0.75rem")};
  margin-left: ${(props) => (props.$isVendor ? "0.75rem" : "0")};
  flex-shrink: 0;
  order: ${(props) => (props.$isVendor ? "2" : "1")};
`;

const MessageContent = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 70%;
  order: ${(props) => (props.$isVendor ? "1" : "2")};
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.$isVendor ? "flex-end" : "flex-start")};
`;

const MessageHeader = styled.div`
  display: flex;
  justify-content: ${(props) => (props.$isVendor ? "flex-end" : "flex-start")};
  align-items: center;
  margin-bottom: 0.25rem;
  width: 100%;
  gap: 0.5rem;
`;

const MessageSender = styled.div`
  font-weight: 600;
  color: #1f2937;
  font-size: 0.875rem;
`;

const MessageTime = styled.div`
  font-size: 0.75rem;
  color: #9ca3af;
`;

const MessageText = styled.div`
  color: ${(props) => (props.$isVendor ? "#ffffff" : "#1f2937")};
  line-height: 1.5;
  word-wrap: break-word;
  white-space: pre-wrap;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  background: ${(props) =>
    props.$isVendor ? "var(--color-primary)" : "#ffffff"};
  border: ${(props) => (props.$isVendor ? "none" : "1px solid #e9ecef")};
  box-shadow: ${(props) =>
    props.$isVendor
      ? "0 2px 8px rgba(44, 95, 45, 0.2)"
      : "0 1px 3px rgba(0, 0, 0, 0.1)"};
  font-size: 0.95rem;
  text-align: left;
  width: fit-content;
  max-width: 100%;
  margin-bottom: ${(props) => (props.$hasImages ? "0.5rem" : "0")};
`;

const MessageImages = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
  max-width: 300px;
`;

const MessageImage = styled.img`
  width: 100%;
  max-width: 300px;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid
    ${(props) => (props.$isVendor ? "rgba(255, 255, 255, 0.2)" : "#e9ecef")};
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const InputArea = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e5e5;
  background: white;

  @media ${device.mobile} {
    padding: 0.75rem 1rem;
  }
`;

const InputContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: flex-end;
  max-width: 768px;
  margin: 0 auto;

  .ant-input {
    border: 1px solid #d1d5db !important;
    border-radius: 8px !important;
    padding: 0.75rem 1rem !important;
    font-size: 0.95rem !important;
    transition: all 0.2s ease !important;
    background: #ffffff !important;
    min-height: 48px !important;
    resize: none !important;
    flex: 1;

    &:focus,
    &:hover {
      border-color: var(--color-primary) !important;
      box-shadow: 0 0 0 2px rgba(44, 95, 45, 0.1) !important;
    }
  }
`;

const ImageUploadSection = styled.div`
  margin-bottom: 1rem;
  max-width: 768px;
  margin-left: auto;
  margin-right: auto;
`;

const AttachmentButton = styled(Button)`
  height: 48px;
  min-width: 48px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${(props) => (props.$active ? "var(--color-primary)" : "#6b7280")};
  border: 1px solid
    ${(props) => (props.$active ? "var(--color-primary)" : "#d1d5db")};
  background: ${(props) =>
    props.$active ? "rgba(44, 95, 45, 0.1)" : "#ffffff"};

  &:hover:not(:disabled) {
    border-color: var(--color-primary);
    color: var(--color-primary);
    background: rgba(44, 95, 45, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SendButton = styled(Button)`
  height: 48px;
  border-radius: 8px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 1rem !important;
  flex-shrink: 0;
  background: #f3f4f6 !important;
  border: 1px solid #d1d5db !important;
  color: #6b7280 !important;

  .ant-btn-icon {
    color: #6b7280 !important;
    filter: none !important;
  }

  &:disabled {
    background: #f3f4f6 !important;
    border-color: #d1d5db !important;
    color: #9ca3af !important;
    cursor: not-allowed !important;
    opacity: 0.6 !important;
  }

  &:hover:not(:disabled) {
    background: #e5e7eb !important;
    border-color: #9ca3b8 !important;
    color: #4b5563 !important;

    .ant-btn-icon {
      color: #4b5563 !important;
    }
  }

  @media ${device.mobile} {
    min-width: 48px;
    padding: 0 !important;

    .ant-btn > span:not(.ant-btn-icon) {
      display: none;
    }
  }
`;

const DisclaimerText = styled.div`
  text-align: center;
  color: #6b7280;
  font-size: 0.875rem;
  margin-top: 0.75rem;
  max-width: 768px;
  margin-left: auto;
  margin-right: auto;
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 3rem 2rem;
`;

const EmptyStateIcon = styled.div`
  font-size: 4rem;
  color: ${(props) => (props.$error ? "#ef4444" : "var(--color-primary)")};
  margin-bottom: 1rem;
`;

const EmptyStateTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.5rem;
`;

const EmptyStateSubtitle = styled.p`
  font-size: 1.125rem;
  color: #64748b;
  margin-bottom: 1.5rem;
  max-width: 600px;
`;

const AccessDeniedContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  padding: 4rem 2rem;
  min-height: calc(100vh - 200px);

  @media ${device.mobile} {
    padding: 3rem 1.5rem;
    min-height: calc(100vh - 150px);
  }
`;

const AccessDeniedContent = styled.div`
  max-width: 600px;
  width: 100%;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const AccessDeniedIconWrapper = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
  box-shadow: 0 8px 24px rgba(239, 68, 68, 0.15);

  .anticon {
    font-size: 4rem;
    color: #dc2626;
  }

  @media ${device.mobile} {
    width: 100px;
    height: 100px;
    margin-bottom: 1.5rem;

    .anticon {
      font-size: 3.5rem;
    }
  }
`;

const AccessDeniedTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 1rem 0;
  font-family: var(--font-primary);

  @media ${device.mobile} {
    font-size: 2rem;
  }
`;

const AccessDeniedSubtitle = styled.p`
  font-size: 1.125rem;
  color: #64748b;
  margin: 0 0 2rem 0;
  line-height: 1.6;
  max-width: 500px;

  @media ${device.mobile} {
    font-size: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const AccessDeniedList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 2rem 0;
  max-width: 600px;
  width: 100%;
  text-align: left;
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  @media ${device.mobile} {
    padding: 1.25rem;
    margin-bottom: 1.5rem;
  }
`;

const AccessDeniedItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 0;
  color: #475569;
  font-size: 1rem;
  line-height: 1.6;

  &:not(:last-child) {
    border-bottom: 1px solid #f1f5f9;
    padding-bottom: 1rem;
    margin-bottom: 0.5rem;
  }

  .anticon {
    color: #ef4444;
    font-size: 1.25rem;
    margin-top: 0.125rem;
    flex-shrink: 0;
  }

  span {
    flex: 1;
    font-weight: 500;
  }

  @media ${device.mobile} {
    font-size: 0.9375rem;
    padding: 0.625rem 0;

    &:not(:last-child) {
      padding-bottom: 0.875rem;
      margin-bottom: 0.375rem;
    }
  }
`;

const AccessDeniedFooter = styled.div`
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 2px solid #e2e8f0;
  max-width: 600px;
  width: 100%;

  @media ${device.mobile} {
    margin-top: 1rem;
    padding-top: 1rem;
  }
`;

const AccessDeniedFooterText = styled.p`
  font-size: 0.9375rem;
  color: #64748b;
  line-height: 1.6;
  margin: 0;
  font-weight: 500;

  @media ${device.mobile} {
    font-size: 0.875rem;
  }
`;

const ContactPopoverContent = styled.div`
  min-width: 250px;
  max-width: 300px;
`;

const ContactPopoverTitle = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--color-primary-darker);
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e2e8f0;

  .anticon {
    color: var(--color-primary);
    font-size: 0.875rem;
  }
`;

const ContactPopoverInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
`;

const ContactPopoverItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
`;

const ContactPopoverLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #64748b;
  min-width: 60px;
  flex-shrink: 0;

  .anticon {
    color: var(--color-primary);
    font-size: 0.75rem;
  }
`;

const ContactPopoverValue = styled.div`
  font-size: 0.8125rem;
  color: #1e293b;
  font-weight: 500;
  word-break: break-word;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  @media ${device.mobile} {
    gap: 0.5rem;
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;

  @media ${device.mobile} {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
`;

const ContactLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #64748b;
  min-width: 80px;

  .anticon {
    color: var(--color-primary);
    font-size: 0.875rem;
  }

  @media ${device.mobile} {
    font-size: 0.8rem;
    min-width: auto;
  }
`;

const ContactValue = styled.div`
  font-size: 0.875rem;
  color: #1e293b;
  font-weight: 500;

  @media ${device.mobile} {
    font-size: 0.85rem;
  }
`;

const ContactLink = styled.a`
  color: var(--color-primary-darker);
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: var(--color-primary);
    text-decoration: underline;
  }
`;

const EmptyText = styled.div`
  color: #64748b;
  font-size: 1rem;
`;

const EmptySubtext = styled.div`
  color: #94a3b8;
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  gap: 1rem;
`;

const LoadingText = styled.div`
  color: #6b7280;
  font-size: 0.9rem;
`;

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  gap: 0.75rem;
`;
